import { App } from 'vue';

type TranslationFunction = (
  key: string,
  interpolations: object | string[]
) => string;

interface I18n {
  locale: string;
  messages: object;
  handleNotFound: TranslationFunction | null;
  $t: TranslationFunction;
}

// An alternative to $t that always spits out English. This is used as the default
// fallback for when a key is not found.
function showEnglish(
  this: I18n,
  key: string,
  interpolations: object | string[] = {}
): string {
  const englishFallback = {
    locale: 'en',
    messages: this.messages,
    handleNotFound: (key: string) => {
      console.error(`i18n key not found "${key}"`);
      let specificKey = key.substring(key.lastIndexOf('.') + 1);
      specificKey = specificKey.replace(/_/g, ' ');
      const capitalized =
        specificKey.charAt(0).toUpperCase() + specificKey.slice(1);

      return capitalized;
    }
  };
  return this.$t.call(englishFallback, key, interpolations);
}

export class VueI18n implements I18n {
  locale: string;
  messages: object;
  handleNotFound: TranslationFunction | null = showEnglish;

  // This is the "bread and butter" function for accessing translations.
  //
  // You can invoke it in your vue components like so:
  //
  //   <p>$t('article.body', { author: 'me' })</p>
  //
  // Assuming a locale of "en", this will access the following translation:
  //
  //   en:
  //     article:
  //       body: test one two - %{author}
  //
  // Your HTML will render as:
  //
  //   <p>test one two - me</p>
  $t(key: string, interpolations: object | string[] = {}): string {
    if (key === '') return '';
    const keys = key.split('.');
    let lookupResult = this.messages[this.locale];

    // Traverse the locale tree based on the given key.
    // "one.two.three" => messages['one']['two']['three']
    for (let i = 0; i <= keys.length; i += 1) {
      // Render literal "one.two.three" if key wasn't found.
      if (!lookupResult) {
        if (this.handleNotFound)
          return this.handleNotFound(key, interpolations);
        return key;
      }

      if (i < keys.length) lookupResult = lookupResult[keys[i]];
    }

    // If the given key didn't lead us to a String, it's probably a mistake.
    if (typeof lookupResult !== 'string') {
      console.error(
        `i18n lookup not a string "${key}" -> ${JSON.stringify(lookupResult)}`
      );
      return key;
    }

    // Perform interpolation.
    for (const name in interpolations) {
      const regexp = new RegExp(`%{\\s*${name}\\s*}`, 'g');
      lookupResult = lookupResult.replace(regexp, interpolations[name]);
    }

    // Done.
    return lookupResult;
  }

  // This is an alternative to $t that lets you provide multiple keys that may
  // or may not exist in the messages object.
  //
  //   <p>$tx(['specific.key', { interp: 'anything' }], 'generic.key')</p>
  $tx(...argsToTry: Array<string | [string, object?]>): string {
    let attempt = '';

    const originalNotFoundHandler = this.handleNotFound;
    try {
      this.handleNotFound = null;

      for (const args of argsToTry) {
        let key: string;

        if (typeof args === 'string') {
          key = args;
          attempt = this.$t(key);
        } else {
          key = args[0];
          attempt = this.$t(...args);
        }

        // attempt === key when $t did not find a suitable translation.
        if (attempt !== key) return attempt;
      }
    } finally {
      this.handleNotFound = originalNotFoundHandler;
    }

    console.error('i18n keys not found', argsToTry);
    return attempt;
  }

  // Check whether key exists. Returns a boolean
  $te(key: string): boolean {
    if (!key) return false;
    const keys = key.split('.');
    let lookup = this.messages[this.locale];

    for (let i = 0; i < keys.length; i++) {
      if (!(keys[i] in lookup)) return false;
      lookup = lookup[keys[i]];
    }

    return true;
  }

  addMessages(messages: object = {}) {
    for (const lang in messages)
      this.messages[lang] = { ...this.messages[lang], ...messages[lang] };
  }

  // Internal, but you can use this instead of createI18n if you want.
  constructor(locale: string, messages: object) {
    // Pull just the language out of complex locale strings.
    // For example, "fr-CA, fr;q=0.9, ja-JP;q=0.8, de;q=0.7, *;q=0.5"
    // should just be "fr".
    this.locale = locale
      .split(',')[0]
      .split(';')[0]
      .split('-')[0];
    this.messages = messages || {};

    // This makes sure we don't error out if given a completely unknown locale.
    if (this.messages[this.locale] === undefined) {
      this.messages[this.locale] = {};
    }
  }

  // See https://v3.vuejs.org/guide/plugins.html#writing-a-plugin
  install(app: App) {
    const globals = {
      $t: this.$t.bind(this),
      $tx: this.$tx.bind(this),
      $te: this.$te.bind(this),
      $i18n: this
    };

    for (const key in globals) {
      app.config.globalProperties[key] = globals[key];
      app.provide(key, globals[key]);
    }
  }
}

// Use this to create a new I18n plugin for your Vue app.
//
//   import { createI18n } from 'hats/plugins/i18n';
//   import { createApp } from 'vue';
//   ...
//   const myApp = createApp(MyComponent);
//   const i18n = createI18n({ locale: 'ja', messages: require('locale/ja.yml') });
//   myApp.use(i18n);
export function createI18n(args: { locale: string; messages: object }) {
  return new VueI18n(args.locale, args.messages);
}
